/**
 * RedirectByArtistId — 301-style redirect from /ragis/:id to /artist/:slug
 *
 * Fetches the artist by UUID, then immediately Navigate(replace) to the
 * canonical /artist/:slug URL. Bots are handled by middleware.ts (server-side
 * 301). This component covers regular users navigating old /ragis/:id links.
 */

import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import PageSkeleton from './ui/PageSkeleton';

export default function RedirectByArtistId() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['artist-by-id', id],
    queryFn: async () => {
      const res = await api.get(`/ragis/${id}`);
      return res.data as { slug?: string; id: string };
    },
    enabled: !!id,
    retry: false,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!isLoading) {
      if (data?.slug) {
        navigate(`/artist/${data.slug}`, { replace: true });
      } else if (data?.id) {
        // No slug yet — stay on a best-effort profile URL
        navigate(`/artist/${data.id}`, { replace: true });
      } else if (isError) {
        navigate('/ragis', { replace: true });
      }
    }
  }, [data, isLoading, isError, navigate]);

  // Skeleton (no copy) while resolving — same shape as the destination page
  return <PageSkeleton />;
}
